import { Box, Stack, Typography } from '@mui/material'
import { ArrowPathIcon } from '@heroicons/react/24/outline'

import { CoachMessageRole } from '../../../../../../../pure-js/types/CoachTypes'
import { stripMarkdownFormatting } from '../../../../../../../pure-js/libs/Common'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { Button } from '../../../../../interaxDesignSystemComponents'
import { COACH_MESSAGE_MAX_WIDTH } from '../coachPageStyles'
import type { PendingCoachMessage } from '../useCoachChat'

type CoachMessageBubbleProps = {
  message: PendingCoachMessage
  /** Present for the user's own messages, so a failed send can be tried again. */
  onRetry?: (message: PendingCoachMessage) => void
}

export const CoachMessageBubble = ({ message, onRetry }: CoachMessageBubbleProps) => {
  const { t } = useTranslation()
  const isUser = message.role === CoachMessageRole.USER

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
      <Box
        sx={{
          maxWidth: COACH_MESSAGE_MAX_WIDTH,
          px: 2,
          py: 1.25,
          borderRadius: 2.5,
          backgroundColor: isUser ? INTERAX_COLORS.sun : INTERAX_COLORS.black28,
          color: isUser ? INTERAX_COLORS.offBlack : INTERAX_COLORS.white,
          opacity: message.failed ? 0.7 : 1,
          border: message.failed ? `1px solid ${INTERAX_COLORS.redError}` : undefined
        }}
      >
        {/* Bubbles are plain text, so any markdown the model slipped in is decoration, not formatting. */}
        <Typography sx={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {stripMarkdownFormatting(message.content)}
        </Typography>
      </Box>

      {message.failed && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
          <Typography sx={{ color: INTERAX_COLORS.redError, fontSize: 12 }}>
            {t('gamer.ariaCoach.sendFailed')}
          </Typography>
          {onRetry && (
            <Button variant="link" size="small" onClick={() => onRetry(message)} icon={<ArrowPathIcon width={14} />}>
              {t('gamer.ariaCoach.retry')}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  )
}
